import { useList } from '@refinedev/core';
import { Skeleton } from 'antd';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { useCountryContext } from '@/contexts/CountryContext';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { RESOURCES } from '@/Resources';

import ServiceOrderItemsSection from './ServiceOrderItemsSection';
import ServiceOrderShowAdmin from './ServiceOrderShowAdmin';
import ServiceOrderShowBillFile from './ServiceOrderShowBillFile';
import ServiceOrderShowBilling from './ServiceOrderShowBilling';
import ServiceOrderShowDetail from './ServiceOrderShowDetail';
import ServiceOrderShowTechnicianResponse from './ServiceOrderShowTechnicianResponse';
import ServiceOrderShowWorkDetail from './ServiceOrderShowWorkDetail';

interface ServiceOrderShowServiceInfoProps {
  serviceOrder: ServiceOrder;
  serviceOrderItems: ServiceOrderItem[];
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  blockEdit: boolean;
  refetch: () => Promise<void>;
  modifingAdminData?: boolean;
  setModifingAdminData?: (modifying: boolean) => void;
}

const ServiceOrderShowServiceInfo = (props: ServiceOrderShowServiceInfoProps) => {
  const { t } = useTranslation();
  const { isBrasilSelected } = useCountryContext();
  const {
    serviceOrder,
    serviceOrderItems,
    setServiceOrderItems,
    blockEdit,
    refetch,
    modifingAdminData,
    setModifingAdminData,
  } = props;
  /**
   * Condiciones de visualización
   */
  // COORDINADA es, a estos fines, una etapa visual/de transición igual a
  // PENDIENTE_DE_COORDINACION: facturación/Nota Fiscal/admin/items recién se
  // habilitan al marcar "Servicio realizado".
  const isCoordinationStage = useMemo(
    () =>
      serviceOrder?.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
      serviceOrder?.status == SERVICE_ORDER_STATUS.COORDINADA,
    [serviceOrder?.status],
  );
  const showAdminData = useMemo(
    () =>
      serviceOrder?.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_FACTURACION ||
      serviceOrder?.status == SERVICE_ORDER_STATUS.FINALIZADA,
    [serviceOrder?.status],
  );
  const {
    result: receptionsResult,
    query: { isLoading: isLoadingReceptions },
  } = useList<Reception>({
    resource: RESOURCES.RECEPTIONS,
    pagination: { pageSize: 1000 },
    queryOptions: {
      enabled: serviceOrder?.id != undefined,
    },
    meta: {
      created_by_service_order: serviceOrder?.id,
    },
  });

  return (
    <>
      <ServiceOrderShowDetail serviceOrder={serviceOrder} blockEdit={blockEdit} refetch={refetch} />
      <ServiceOrderShowWorkDetail
        serviceOrder={serviceOrder}
        blockEdit={blockEdit}
        refetch={refetch}
      />

      {!isCoordinationStage && (
        <>
          <ServiceOrderShowBilling
            serviceOrder={serviceOrder}
            blockEdit={blockEdit}
            refetch={refetch}
          />

          {/* Nota Fiscal (Brasil): upload inmediato, sin pasar por una pantalla de Edit */}
          {isBrasilSelected &&
            (serviceOrder.bill_file || serviceOrder.status === SERVICE_ORDER_STATUS.REALIZADA) && (
              <ServiceOrderShowBillFile serviceOrder={serviceOrder} refetch={refetch} />
            )}

          {/* Conformidad del técnico (link): no aplica en Brasil */}
          {!isBrasilSelected && <ServiceOrderShowTechnicianResponse serviceOrder={serviceOrder} />}
          {/* Admin ARG: paso clásico de horas/km */}
          {showAdminData && !isBrasilSelected && (
            <ServiceOrderShowAdmin
              serviceOrder={serviceOrder}
              refetch={refetch}
              blockEdit={blockEdit}
              modifingAdminData={modifingAdminData}
              setModifingAdminData={setModifingAdminData}
            />
          )}

          <ServiceOrderItemsSection
            serviceOrder={serviceOrder}
            serviceOrderItems={serviceOrderItems}
            setServiceOrderItems={setServiceOrderItems}
            blockEdit={blockEdit}
            refetch={refetch}
          />
          {isLoadingReceptions ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : receptionsResult?.data && receptionsResult.data.length > 0 ? (
            <ShowSection title={t('Recepciones asociadas')}>
              <ul>
                {receptionsResult.data.map(reception => (
                  <li key={reception.id}>
                    <div className="ServiceOrderShowReceptionRow">
                      <LinkTextField
                        local
                        to={`/${RESOURCES.RECEPTIONS}/show/${reception.id}`}
                        label={`${t('Recepción')} #${reception.id}`}
                      />
                      {reception.status != undefined && (
                        <PackageStateTag packageStatus={reception.status} />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </ShowSection>
          ) : null}
        </>
      )}
    </>
  );
};

export default ServiceOrderShowServiceInfo;
